import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../Layout';
import { listBaskets, getCompetitorData, parseCsvFile } from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';

/* ──────────────────────────────────────────── */
/* Best Sellers Rank — port of best_sellers.py  */
/* Weekly BSR table with movement indicators    */
/* ──────────────────────────────────────────── */

function BestSellersRank() {
    const [baskets, setBaskets] = useState([]);
    const [selectedBasket, setSelectedBasket] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [csvName, setCsvName] = useState(null);

    const accountId = localStorage.getItem('selectedAccountValue') || '';

    const loadBaskets = useCallback(async () => {
        if (!accountId) return;
        try { const res = await listBaskets(accountId); setBaskets(res.data || []); } catch { }
    }, [accountId]);

    useEffect(() => { loadBaskets(); }, [loadBaskets]);

    const loadData = useCallback(async () => {
        if (!selectedBasket || !accountId || selectedBasket.id === 'csv') return;
        setLoading(true);
        try { const res = await getCompetitorData(selectedBasket.id, accountId); setData(res.data || []); }
        catch { } finally { setLoading(false); }
    }, [selectedBasket, accountId]);

    useEffect(() => { loadData(); }, [loadData]);

    // CSV upload
    const handleCsvUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true);
        try {
            const res = await parseCsvFile(file);
            setData(res.data || []);
            setCsvName(file.name);
            setSelectedBasket({ id: 'csv', name: file.name });
        } catch { }
        finally { setLoading(false); }
    };
    const handleClearCsv = () => { setCsvName(null); setSelectedBasket(null); setData([]); };

    // Build BSR table grouped by week
    const weeks = [...new Set(data.map(r => r.week).filter(Boolean))].sort((a, b) => a - b);
    const asins = [...new Set(data.map(r => r.asin).filter(Boolean))];

    const getMovement = (asin, weekIdx) => {
        if (weekIdx === 0) return null;
        const curr = data.find(r => r.asin === asin && r.week === weeks[weekIdx]);
        const prev = data.find(r => r.asin === asin && r.week === weeks[weekIdx - 1]);
        if (!curr.rank_value || !prev.rank_value) return null;
        const diff = prev.rank_value - curr.rank_value;
        if (diff > 0) return { dir: 'up', diff, color: 'text-success' };
        if (diff < 0) return { dir: 'down', diff: Math.abs(diff), color: 'text-danger' };
        return { dir: 'same', diff: 0, color: 'text-muted' };
    };

    return (
        <Layout>
            <div className="container-fluid py-4">
                <h2 className="fw-bold mb-4">Best Sellers Rank</h2>

                <div className="row mb-3">
                    <div className="col-md-4">
                        <select className="form-select" value={selectedBasket?.id || ''}
                            onChange={e => setSelectedBasket(baskets.find(b => b.id === parseInt(e.target.value)) || null)}>
                            <option value="">Select basket...</option>
                            {baskets.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <div className="d-flex align-items-center gap-2">
                            <label className="btn btn-outline-info btn-sm mb-0" style={{ cursor: 'pointer' }}>
                                <i className="bi bi-filetype-csv me-1"></i>Upload CSV
                                <input type="file" accept=".csv" hidden onChange={handleCsvUpload} />
                            </label>
                            {csvName && (
                                <span className="badge bg-success d-flex align-items-center gap-1">
                                    {csvName}
                                    <i className="bi bi-x-lg" style={{ cursor: 'pointer' }} onClick={handleClearCsv}></i>
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {loading && <div className="text-center"><div className="spinner-border" /></div>}

                {data.length > 0 && (
                    <div className="card">
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>ASIN</th>
                                            <th>Brand</th>
                                            <th>Sub-category</th>
                                            {weeks.map(w => <th key={w}>Week {w}</th>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {asins.map(asin => {
                                            const latest = data.filter(r => r.asin === asin).sort((a, b) => new Date(b.date) - new Date(a.date))[0];
                                            return (
                                                <tr key={asin}>
                                                    <td className="fw-bold">{asin}</td>
                                                    <td>{latest?.brand || '—'}</td>
                                                    <td><small>{latest?.sub_category_name || '—'}</small></td>
                                                    {weeks.map((w, wi) => {
                                                        const row = data.find(r => r.asin === asin && r.week === w);
                                                        const mov = getMovement(asin, wi);
                                                        return (
                                                            <td key={w} className="text-center">
                                                                {row.rank_value != null ? (
                                                                    <>
                                                                        <span>#{row.rank_value.toLocaleString()}</span>
                                                                        {mov && (
                                                                            <small className={`d-block ${mov.color}`}>
                                                                                {mov.dir === 'up' && `^ ${mov.diff}`}
                                                                                {mov.dir === 'down' && `v ${mov.diff}`}
                                                                                {mov.dir === 'same' && '—'}
                                                                            </small>
                                                                        )}
                                                                    </>
                                                                ) : '—'}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default BestSellersRank;
